﻿// Modules
import idiCore from "./modules/core/core.module";
import idiAdministration from "./modules/administration/administration.module";
import idiAuthentication from "./modules/authentication/authentication.module";
import idiQuoteWizard from "./modules/quotewizard/quoteWizard.module";
import idiRepositories from './modules/repositories/repository.module';
import idiAccount from "./modules/account/account.module";

// Controllers
import {ShellController} from "./modules/shell/shell.controller";
import {AdministrationFrameController} from "./modules/administration/administrationFrame.controller";
import {CallbackController} from "./modules/authentication/callback/callback.controller";
import {AuthenticationFrameController} from "./modules/authentication/authenticationFrame.controller";
import {QuoteWizardFrameController} from "./modules/quotewizard/quotewizardFrame.controller";
import {ErrorViewController} from './modules/shell/errorView/errorView.controller';

// Services
import {IErrorRedirectService} from './modules/core/services/errorRedirectService/errorRedirect.service';
import * as Session from './modules/core/services/sessionService/session.service';

// Providers
import * as Authorization from './modules/core/services/authorizationService/authorization.provider';
import * as RecaptchaServiceProvider from './modules/core/directives/recaptcha/recaptcha.provider';

// Interceptors
import {AuthenticationHttpInterceptor} from './modules/authentication/authenticationHttpInterceptor';
import {Http404Interceptor} from './modules/core/http404Interceptor';
import {HttpErrorRedirectInterceptor} from './modules/core/services/errorRedirectService/httpErrorRedirectInterceptor';
import {IErrorInformation} from './modules/core/services/errorRedirectService/errorInformation.model';

// note: ngConstants.js is generated by the gulp transform:local, transform:test, transform:prod, and transform:dev tasks
import "./ngConstants";
import IAppSettings from './iApp.settings';

import {QuoteWizardStateService} from './modules/quoteWizard/quoteWizardState.service';
import {IQuoteWizardPageParams} from './modules/quotewizard/quoteWizardPageParams';
import {UserAccountState, UserAccountStateService} from './modules/core/services/userAccountStateService/userAccountState.service';
import {AccountFrameController} from './modules/account/accountFrame.controller';
import {ScreenLockService} from './modules/core/services/screenLockService/screenLock.service';
import { Roles } from "./modules/models/constants/authentication.constants";


const app = angular.module("idi.marketplace", [
    "ui.router",
    "ui.mask",
    "ngAnimate",
    "ghiscoding.validation",
    "pascalprecht.translate", // required by ghiscoding.validation (form validation library)
    "idi.marketplace.appSettings",
    idiCore,
    idiAccount,
    idiAdministration,
    idiAuthentication,
    idiQuoteWizard,
    idiRepositories,
    "angular-perfect-scrollbar-2",
    "angular-typed", // Modified from NPM Package
    "ui.bootstrap",
    "auth0.auth0"
    ]);

app.controller("shell.controller", ShellController);
app.controller("accountFrame.controller", AccountFrameController);
app.controller("administrationFrame.controller", AdministrationFrameController);
app.controller("callback.controller", CallbackController);
app.controller("authenticationFrame.controller", AuthenticationFrameController);
app.controller('errorView.controller', ErrorViewController);
app.controller('quoteWizardFrame.controller', QuoteWizardFrameController);

// routes
app.config([
    '$stateProvider', '$urlRouterProvider', "$locationProvider", ($stateProvider: angular.ui.IStateProvider, $urlRouterProvider: angular.ui.IUrlRouterProvider, $locationProvider: any) => {

    

        $stateProvider
            .state('shellFrame',
            {
                url: '/',
                abstract: true,
                template: `<ui-view/>`,
                resolve: {}
            })
            .state('shellFrame.accountFrame',
            {
                url: "account",
                abstract: true,
                templateUrl: "modules/account/accountFrame.html",
                controller: "accountFrame.controller",
                controllerAs: "vm",
                data: { claimsProfile: { allowAnonymousAccess: true } },
                resolve: {}
            })
            .state('shellFrame.errorView',
            {
                url: 'error',
                templateUrl: 'modules/shell/errorView/error.view.html',
                controller: 'errorView.controller',
                controllerAs: 'vm',
                data: { claimsProfile: { allowAnonymousAccess: true } },
                params: { errorInformation: {} as IErrorInformation },
                resolve: {}
            })
            .state('shellFrame.administrationFrame',
            {
                url: "administration",
                templateUrl: "modules/administration/administrationFrame.html",
                controller: "administrationFrame.controller",
                controllerAs: "vm",
                data: { claimsProfile: { 
                            allowAnonymousAccess: false, 
                            claims: [Roles.Employee]
                        },
                        redirectToSignOn: 'shellFrame.authenticationFrame.login'
                },
                resolve: {}
            })
            .state('shellFrame.quoteWizardFrame',
            {
                url: "wizard",
                params: { userId: undefined, redirectStateName: undefined } as IQuoteWizardPageParams,
                abstract: true,
                templateUrl: "modules/quoteWizard/quoteWizardFrame.html",
                controller: "quoteWizardFrame.controller",
                controllerAs: "vm",
                data: { claimsProfile: { allowAnonymousAccess: true } },
                resolve: getQuoteWizardResolver()
            })
            .state('shellFrame.authenticationFrame', {
                url: "authentication",
                abstract: true,
                templateUrl: 'modules/authentication/authenticationFrame.html',
                controller: 'authenticationFrame.controller',
                controllerAs: 'vm',
                data: { claimsProfile: { allowAnonymousAccess: true } },
                resolve: {}
            });

      $urlRouterProvider.otherwise("wizard");

      $locationProvider.html5Mode(true);

    }
]);

function getQuoteWizardResolver(): any {
    return {
        initialized: [ 'quoteWizardStateService', '$stateParams', 'userAccountStateService', '$state', '$timeout', '$q', 'screenLockService',
            (quoteWizardStateService: QuoteWizardStateService,
             $stateParams: IQuoteWizardPageParams,
             userAccountStateService: UserAccountStateService,
             $state: angular.ui.IStateService,
             $timeout: ng.ITimeoutService,
             $q: ng.IQService,
             screenLockService: ScreenLockService) =>
            {
                screenLockService.broadcast(1);
                return userAccountStateService.getUserState().then((userState: UserAccountState) => {
                    switch (userState) {
                        case UserAccountState.newOrQuoteInProgress:
                            // redirect to welcome page (wizard initialize process will handle this)
                            return quoteWizardStateService.initialize();
                        case UserAccountState.bound:
                            // redirect to account summary
                            $timeout(() => $state.go('shellFrame.accountFrame.accountSummary'));
                            screenLockService.broadcast(0);
                            return $q.reject();
                        case UserAccountState.quotedOrReferred:
                            // redirect to returning user info page
                            $stateParams.redirectStateName = 'shellFrame.quoteWizardFrame.wizard.returningUserInfo';
                            quoteWizardStateService.initializeForReturningUserAfterWizardCompletion();
                            return $q.resolve();
                    }
                });
            }]
    };
}

// translation
app.config(translateConfig);

// general config
app.config(['$httpProvider',  'intrepidAuthorizationServiceProvider', 'appSettings', 'recaptchaServiceProvider', 'angularAuth0Provider',
    (
        $httpProvider: ng.IHttpProvider,
        authorizationProvider: Authorization.IAuthorizationServiceProvider,
        appSettings: IAppSettings,
        recaptchaServiceProvider: RecaptchaServiceProvider.IRecaptchaProvider,
        angularAuth0Provider: any
    ) => {

        // configure httpinterceptor for inserting jwt header -- this is how we send the BTS bearer token to our REST services on every call
        $httpProvider.interceptors.push(AuthenticationHttpInterceptor.factory);

        // configure http interceptor for http errors.
        // NOTE: this one must be registered BEFORE the 404 handler so that xlated 404s are not handled as errors (interceptors for responses are processed in reverse order)
        $httpProvider.interceptors.push(HttpErrorRedirectInterceptor.factory);

        // configure httpinterceptor for translating 404's for unfound resources to empty responses
        $httpProvider.interceptors.push(Http404Interceptor.factory);

        // this prevent the BTS Identity Cookie from being transmitted.  Since the JWT is in the header, legitimate calls still work, but disabling the cookie protects us from CSRF
        $httpProvider.defaults.withCredentials = false;

        // by default, all routes require the user is authenticated, but do not require any specific claims
        authorizationProvider.setDefaultClaimsProfile({ allowAnonymousAccess: false } as Session.IClaimsProfile);

        recaptchaServiceProvider.setSiteKey("6Ld5GR4UAAAAAKDkoyyE74fb1E9FoEaqaHLGF9w1");

        angularAuth0Provider.init({
            audience: appSettings.auth0.audience,
            domain: appSettings.auth0.domain,
            clientID: appSettings.auth0.clientID,
            responseType: 'token id_token',
            redirectUri: appSettings.auth0.redirectUri,
            scope: 'openid profile'
        });
    }
]);

//translateConfig initializes ngtranslate, which is required by the validation library
translateConfig.$inject = ["$translateProvider"];
// intentionally using the "function" syntax and not lambdas -- I think that this is one of those rare cases where the service expects for the value of "this" to have been applied
function translateConfig($translateProvider: any) {
    $translateProvider.useStaticFilesLoader({
        // prefix should always point to the folder that contains the folder that contains the  xx.json locale files that ship with angular-validation-ghiscoding
        prefix: "./vendor/angular-validation-ghiscoding/locales/validation/",
        suffix: ".json"
    });
    $translateProvider.preferredLanguage("en");
    $translateProvider.useSanitizeValueStrategy("escape");
}

app.run([ 'intrepidAuthorizationService', 'errorRedirectService',
    (
        authService: Authorization.IAuthorizationService,
        errorRedirectService: IErrorRedirectService
    ) => {

        authService.useAuthorizationService();

        

        errorRedirectService.setErrorStateName('shellFrame.errorView');
    }
]);
